import { useState, useRef, useEffect, useCallback } from 'react';
import { useReportBuilderStore, getAllowedAggregations } from '../../stores/reportBuilderStore';
import type { AnnotatedColumn } from '../../stores/reportBuilderStore';
import type { FilterOperator, AggregationType } from '../../types/reportBuilder';
import { OPERATOR_LABELS, HAVING_OPERATORS, RELATIVE_DATE_PRESETS, WINDOW_FUNC_LABELS } from '../../types/reportBuilder';
import {
  Rows, Calculator, Funnel, ArrowsDownUp, Calendar, X, Plus,
  ArrowUp, ArrowDown, ChartLine,
} from '@phosphor-icons/react';
import WindowFunctionBuilder from './WindowFunctionBuilder';
import { FIELD_COLORS, pillClasses } from './fieldEncoding';
import { FEATURE_FLAGS } from '../../constants/featureFlags';

const NO_VALUE_OPS: FilterOperator[] = ['is_null', 'is_not_null'];
const PRESET_OPS: FilterOperator[] = ['relative_date'];

// ─── Field Pill ─────────────────────────────────────────────────────────────

function FieldPill({
  label, type, onRemove, badge, icon,
}: {
  label: string;
  type: keyof typeof FIELD_COLORS;
  onRemove?: () => void;
  badge?: string;
  icon?: React.ReactNode;
}) {
  return (
    <span className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium ${pillClasses(type)}`}>
      {icon}
      <span className="max-w-[180px] truncate">{label}</span>
      {badge && (
        <span className="rounded bg-white/60 px-1 py-0.5 text-[9px] font-semibold uppercase">{badge}</span>
      )}
      {onRemove && (
        <button onClick={onRemove} className="ml-0.5 rounded-sm hover:bg-black/10"><X size={10} /></button>
      )}
    </span>
  );
}

// ─── Filter Pill with Popover ───────────────────────────────────────────────

function FilterPill({ filterId }: { filterId: string }) {
  const { filters, getFilterableColumns, updateFilter, removeFilter } = useReportBuilderStore();
  const filter = filters.find(f => f.id === filterId);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (!filter) return null;

  const filterableCols = getFilterableColumns();
  const col = filterableCols.find(c => c.refKey === filter.column);

  const opLabel = OPERATOR_LABELS[filter.operator] || filter.operator;
  const valStr = NO_VALUE_OPS.includes(filter.operator) ? ''
    : Array.isArray(filter.value) ? filter.value.join(', ') : String(filter.value || '...');
  const displayText = `${col?.label || filter.column} ${opLabel}${valStr ? ` ${valStr}` : ''}`;

  return (
    <div ref={ref} className="relative inline-flex">
      <div onClick={() => setOpen(!open)} role="button" tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setOpen(!open); }}
        className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium cursor-pointer ${pillClasses('filter')} hover:brightness-95`}
      >
        <Funnel size={10} />
        <span className="max-w-[220px] truncate">{displayText}</span>
        <button onClick={(e) => { e.stopPropagation(); removeFilter(filterId); }}
          className="ml-0.5 rounded-sm hover:bg-black/10"><X size={10} /></button>
      </div>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-72 rounded-md border border-gray-200 bg-white p-3 shadow-lg space-y-2">
          <FilterEditor filterId={filterId} col={col} filterableCols={filterableCols} />
        </div>
      )}
    </div>
  );
}

// ─── Filter Editor (inside popover) ─────────────────────────────────────────

function FilterEditor({
  filterId, col, filterableCols,
}: {
  filterId: string;
  col: AnnotatedColumn | undefined;
  filterableCols: AnnotatedColumn[];
}) {
  const { filters, updateFilter } = useReportBuilderStore();
  const filter = filters.find(f => f.id === filterId);
  if (!filter) return null;

  const handleColumnChange = (refKey: string) => {
    const newCol = filterableCols.find(c => c.refKey === refKey);
    updateFilter(filterId, { column: refKey, operator: newCol?.allowed_operators[0] || 'eq', value: '' });
  };

  return (
    <>
      <div>
        <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Column</label>
        <select value={filter.column} onChange={(e) => handleColumnChange(e.target.value)}
          className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none">
          {filterableCols.map(c => <option key={c.refKey} value={c.refKey}>{c.sourceModelName} / {c.label}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Operator</label>
        <select value={filter.operator}
          onChange={(e) => {
            const op = e.target.value as FilterOperator;
            const newValue = NO_VALUE_OPS.includes(op) ? null : PRESET_OPS.includes(op) ? RELATIVE_DATE_PRESETS[0]?.value ?? '' : '';
            updateFilter(filterId, { operator: op, value: newValue });
          }}
          className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none">
          {(col?.allowed_operators || []).map(op => <option key={op} value={op}>{OPERATOR_LABELS[op] || op}</option>)}
          {col && (col.data_type === 'date' || col.data_type === 'timestamp') && (
            <option value="relative_date">{OPERATOR_LABELS.relative_date}</option>
          )}
        </select>
      </div>
      {!NO_VALUE_OPS.includes(filter.operator) && (
        <div>
          <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Value</label>
          <FilterValueInput filterId={filterId} col={col} operator={filter.operator} value={filter.value} />
        </div>
      )}
    </>
  );
}

// ─── Filter Value Input (type-aware) ────────────────────────────────────────

function FilterValueInput({
  filterId, col, operator, value,
}: {
  filterId: string;
  col: AnnotatedColumn | undefined;
  operator: FilterOperator;
  value: unknown;
}) {
  const { updateFilter } = useReportBuilderStore();

  if (operator === 'relative_date') {
    return (
      <select value={String(value ?? '')}
        onChange={(e) => updateFilter(filterId, { value: e.target.value })}
        className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none">
        {RELATIVE_DATE_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
      </select>
    );
  }

  if (operator === 'between') {
    const arr = Array.isArray(value) ? value : ['', ''];
    const type = col?.data_type === 'date' ? 'date' : col?.data_type === 'integer' || col?.data_type === 'decimal' ? 'number' : 'text';
    return (
      <div className="flex gap-1">
        <input type={type} value={String(arr[0] ?? '')}
          onChange={(e) => updateFilter(filterId, { value: [e.target.value, arr[1] ?? ''] })}
          className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none" placeholder="From" />
        <input type={type} value={String(arr[1] ?? '')}
          onChange={(e) => updateFilter(filterId, { value: [arr[0] ?? '', e.target.value] })}
          className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none" placeholder="To" />
      </div>
    );
  }

  if (operator === 'in' || operator === 'not_in') {
    if (col?.filter_options?.length) {
      const selected = Array.isArray(value) ? value as string[] : [];
      return (
        <div className="space-y-1 max-h-32 overflow-y-auto rounded border border-gray-300 bg-white p-1.5">
          {col.filter_options.map(opt => (
            <label key={opt} className="flex items-center gap-1.5 cursor-pointer text-xs text-gray-700 hover:bg-gray-50 rounded px-1 py-0.5">
              <input type="checkbox" checked={selected.includes(opt)}
                onChange={(e) => {
                  const next = e.target.checked ? [...selected, opt] : selected.filter(v => v !== opt);
                  updateFilter(filterId, { value: next });
                }}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-3 w-3" />
              {opt}
            </label>
          ))}
        </div>
      );
    }
    return (
      <input type="text" value={Array.isArray(value) ? value.join(', ') : String(value ?? '')}
        onChange={(e) => updateFilter(filterId, { value: e.target.value.split(',').map(v => v.trim()).filter(Boolean) })}
        className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none" placeholder="Comma-separated values" />
    );
  }

  if (col?.filter_input === 'select' && col.filter_options?.length && (operator === 'eq' || operator === 'ne')) {
    return (
      <select value={String(value ?? '')} onChange={(e) => updateFilter(filterId, { value: e.target.value })}
        className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none">
        <option value="">Select a value...</option>
        {col.filter_options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    );
  }

  const inputType = col?.data_type === 'date' ? 'date' : col?.data_type === 'timestamp' ? 'datetime-local'
    : col?.data_type === 'integer' || col?.data_type === 'decimal' ? 'number' : 'text';

  return (
    <input type={inputType} value={String(value ?? '')}
      onChange={(e) => updateFilter(filterId, {
        value: inputType === 'number' ? (e.target.value ? Number(e.target.value) : '') : e.target.value,
      })}
      className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none" placeholder="Value" />
  );
}

// ─── Measure Pill (with aggregation selector) ───────────────────────────────

function MeasurePill({ alias }: { alias: string }) {
  const { selectedMeasures, removeMeasure, updateMeasure, getMeasureColumns, getDimensionColumns } = useReportBuilderStore();
  const m = selectedMeasures.find(x => x.alias === alias);
  if (!m) return null;

  // Look up column from both measure and dimension columns (supports ad-hoc aggregation)
  const measureCols = getMeasureColumns();
  const dimensionCols = getDimensionColumns();
  const col = measureCols.find(c => c.refKey === m.column) ?? dimensionCols.find(c => c.refKey === m.column);
  const aggTypes = col ? getAllowedAggregations(col) : [];

  const handleAggChange = (agg: AggregationType) => {
    updateMeasure(alias, { column: m.column, aggregation: agg, alias: `${agg}_${col?.key || m.column}` });
  };

  return (
    <span className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium ${pillClasses('measure')}`}>
      <span className="max-w-[120px] truncate">{col?.label || m.column}</span>
      {aggTypes.length > 1 ? (
        <select value={m.aggregation} onChange={(e) => handleAggChange(e.target.value as AggregationType)}
          className="rounded bg-white/60 px-1 py-0 text-[9px] font-semibold uppercase border-0 focus:ring-0 cursor-pointer">
          {aggTypes.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
      ) : (
        <span className="rounded bg-white/60 px-1 py-0.5 text-[9px] font-semibold uppercase">{m.aggregation}</span>
      )}
      <button onClick={() => removeMeasure(alias)} className="ml-0.5 rounded-sm hover:bg-black/10"><X size={10} /></button>
    </span>
  );
}

// ─── Sort Pill ──────────────────────────────────────────────────────────────

function SortPill({ sortId }: { sortId: string }) {
  const { sorts, updateSort, removeSort } = useReportBuilderStore();
  const s = sorts.find(x => x.id === sortId);
  if (!s) return null;

  return (
    <span className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium ${pillClasses('sort')}`}>
      {s.direction === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
      <span className="max-w-[140px] truncate">{s.column}</span>
      <button onClick={() => updateSort(s.id, { direction: s.direction === 'asc' ? 'desc' : 'asc' })}
        className="rounded bg-white/60 px-1 py-0.5 text-[9px] font-semibold uppercase hover:bg-white/80">
        {s.direction}
      </button>
      <button onClick={() => removeSort(s.id)} className="ml-0.5 rounded-sm hover:bg-black/10"><X size={10} /></button>
    </span>
  );
}

// ─── Having Pill with Popover ────────────────────────────────────────────────

function HavingPill({ havingId }: { havingId: string }) {
  const { havingFilters, selectedMeasures, updateHavingFilter, removeHavingFilter } = useReportBuilderStore();
  const hf = havingFilters.find(f => f.id === havingId);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (!hf) return null;

  const opLabel = OPERATOR_LABELS[hf.operator] || hf.operator;
  const valStr = hf.operator === 'between' && Array.isArray(hf.value)
    ? `${hf.value[0]} - ${hf.value[1]}`
    : String(hf.value ?? '...');
  const displayText = `${hf.measure_alias} ${opLabel} ${valStr}`;

  return (
    <div ref={ref} className="relative inline-flex">
      <div onClick={() => setOpen(!open)} role="button" tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setOpen(!open); }}
        className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium cursor-pointer ${pillClasses('measure')} hover:brightness-95`}
      >
        <Funnel size={10} />
        <span className="max-w-[220px] truncate">{displayText}</span>
        <button onClick={(e) => { e.stopPropagation(); removeHavingFilter(havingId); }}
          className="ml-0.5 rounded-sm hover:bg-black/10"><X size={10} /></button>
      </div>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-72 rounded-md border border-gray-200 bg-white p-3 shadow-lg space-y-2">
          <div>
            <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Measure</label>
            <select value={hf.measure_alias}
              onChange={(e) => updateHavingFilter(havingId, { measure_alias: e.target.value })}
              className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none">
              {selectedMeasures.map(m => <option key={m.alias} value={m.alias}>{m.alias}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Operator</label>
            <select value={hf.operator}
              onChange={(e) => updateHavingFilter(havingId, { operator: e.target.value as FilterOperator })}
              className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none">
              {HAVING_OPERATORS.map(op => <option key={op} value={op}>{OPERATOR_LABELS[op] || op}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Value</label>
            {hf.operator === 'between' ? (
              <div className="flex gap-1">
                <input type="number" value={String((Array.isArray(hf.value) ? hf.value[0] : '') ?? '')}
                  onChange={(e) => {
                    const arr = Array.isArray(hf.value) ? hf.value : ['', ''];
                    updateHavingFilter(havingId, { value: [e.target.value ? Number(e.target.value) : '', arr[1] ?? ''] });
                  }}
                  className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none" placeholder="From" />
                <input type="number" value={String((Array.isArray(hf.value) ? hf.value[1] : '') ?? '')}
                  onChange={(e) => {
                    const arr = Array.isArray(hf.value) ? hf.value : ['', ''];
                    updateHavingFilter(havingId, { value: [arr[0] ?? '', e.target.value ? Number(e.target.value) : ''] });
                  }}
                  className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none" placeholder="To" />
              </div>
            ) : (
              <input type="number" value={String(hf.value ?? '')}
                onChange={(e) => updateHavingFilter(havingId, { value: e.target.value ? Number(e.target.value) : '' })}
                className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none" placeholder="Value" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Shelf Row ──────────────────────────────────────────────────────────────

function ShelfRow({
  label, icon, children, emptyText, onAdd,
}: {
  label: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  emptyText: string;
  onAdd?: () => void;
}) {
  const hasChildren = Array.isArray(children) ? children.filter(Boolean).length > 0 : !!children;
  return (
    <div className="flex items-start gap-2">
      <div className="flex w-14 flex-shrink-0 items-center gap-1 pt-1 text-[11px] font-semibold text-gray-500">
        {icon}
        {label}
      </div>
      <div className="flex min-h-[28px] flex-1 flex-wrap items-center gap-1.5 rounded border border-dashed border-gray-300 bg-gray-50/50 px-2 py-1">
        {hasChildren ? children : (
          <span className="text-[11px] text-gray-400 italic">{emptyText}</span>
        )}
      </div>
      {onAdd && (
        <button onClick={onAdd} className="flex-shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" title={`Add ${label.toLowerCase()}`}>
          <Plus size={12} />
        </button>
      )}
    </div>
  );
}

// ─── EncodingShelves (main export) ──────────────────────────────────────────

export default function EncodingShelves() {
  const {
    selectedModel, selectedDimensions, selectedMeasures, filters, havingFilters, sorts, timeGroupings, windowFunctions,
    getDimensionColumns, getDateColumns, getFilterableColumns, getSortableColumns,
    removeDimension, removeTimeGrouping, removeWindowFunction,
    addFilter, addHavingFilter, addSort,
  } = useReportBuilderStore();

  const [showWfBuilder, setShowWfBuilder] = useState(false);

  if (!selectedModel) return null;

  const dimensionCols = getDimensionColumns();
  const dateColumns = getDateColumns();
  const filterableCols = getFilterableColumns();

  const handleAddFilter = useCallback(() => {
    const firstCol = filterableCols[0];
    if (!firstCol) return;
    addFilter({ column: firstCol.refKey, operator: firstCol.allowed_operators[0] || 'eq', value: '' });
  }, [filterableCols, addFilter]);

  const handleAddHavingFilter = useCallback(() => {
    const firstMeasure = selectedMeasures[0];
    if (!firstMeasure) return;
    addHavingFilter({ measure_alias: firstMeasure.alias, operator: 'gt', value: '' });
  }, [selectedMeasures, addHavingFilter]);

  const sortableCols = getSortableColumns();

  const handleAddSort = useCallback(() => {
    const firstCol = sortableCols[0];
    if (!firstCol) return;
    addSort({ column: firstCol.key, direction: 'asc' });
  }, [sortableCols, addSort]);

  return (
    <div className="space-y-1">
      {/* Rows shelf — dimensions + time groupings */}
      <ShelfRow label="Rows" icon={<Rows size={12} />} emptyText="Click fields to add">
        {selectedDimensions.map(refKey => {
          const col = dimensionCols.find(c => c.refKey === refKey);
          return (
            <FieldPill key={refKey} label={col?.label || refKey} type="dimension"
              onRemove={() => removeDimension(refKey)} />
          );
        })}
        {timeGroupings.map(tg => {
          const col = dateColumns.find(c => c.refKey === tg.column);
          const label = col ? `${col.label} (${tg.granularity.charAt(0).toUpperCase() + tg.granularity.slice(1)})` : tg.column;
          return (
            <FieldPill key={`${tg.column}_${tg.granularity}`} label={label} type="time"
              icon={<Calendar size={10} />}
              onRemove={() => removeTimeGrouping(tg.column)} />
          );
        })}
      </ShelfRow>

      {/* Values shelf — measures */}
      <ShelfRow label="Values" icon={<Calculator size={12} />} emptyText="Click measure fields to add">
        {selectedMeasures.map(m => (
          <MeasurePill key={m.alias} alias={m.alias} />
        ))}
      </ShelfRow>

      {/* Filters shelf */}
      <ShelfRow label="Filters" icon={<Funnel size={12} />} emptyText="Right-click fields to filter" onAdd={handleAddFilter}>
        {filters.map(f => <FilterPill key={f.id} filterId={f.id} />)}
      </ShelfRow>

      {/* Having shelf — visible when measures exist */}
      {FEATURE_FLAGS.HAVING_FILTERS && selectedMeasures.length > 0 && (
        <ShelfRow label="Having" icon={<Funnel size={12} />} emptyText="Filter on aggregated values" onAdd={handleAddHavingFilter}>
          {havingFilters.map(hf => <HavingPill key={hf.id} havingId={hf.id} />)}
        </ShelfRow>
      )}

      {/* Window Functions shelf — visible when measures exist */}
      {FEATURE_FLAGS.WINDOW_FUNCTIONS && selectedMeasures.length > 0 && (
        <ShelfRow label="Window" icon={<ChartLine size={12} />} emptyText="Add ranking or window function" onAdd={() => setShowWfBuilder(true)}>
          {windowFunctions.map(wf => (
            <FieldPill key={wf.key} label={`${WINDOW_FUNC_LABELS[wf.function] ?? wf.function}${wf.column ? ` (${wf.column})` : ''}`}
              type="measure" icon={<ChartLine size={10} />}
              onRemove={() => removeWindowFunction(wf.key)} />
          ))}
        </ShelfRow>
      )}

      {/* Sort shelf */}
      <ShelfRow label="Sort" icon={<ArrowsDownUp size={12} />} emptyText="Add sort from fields" onAdd={handleAddSort}>
        {sorts.map(s => <SortPill key={s.id} sortId={s.id} />)}
      </ShelfRow>

      {showWfBuilder && <WindowFunctionBuilder onClose={() => setShowWfBuilder(false)} />}
    </div>
  );
}
